// Service-worker registration, the update prompt, and the install hint.
//
// Every branch here exists because a naive version misbehaved in the field.
// Change it only with a reason, and re-run the S3 smoke check afterwards.


import { ref } from 'vue';
import { toast, dismiss } from './util.js';
import { isNative, appVersion } from './native.js';
import { WEB_BUILD, APP_NAME } from '../../app-config.js';

/**
 * @param {() => boolean} isSafeToReload  called before an update reload; return
 *        false while the user is mid-task and the reload will be deferred.
 */
export function initPWA({ isSafeToReload = () => true } = {}) {
  // Native builds bundle their assets: no service worker, no install prompt, no
  // update toast — and a stray location.reload() from a controllerchange would
  // be actively harmful mid-task. The whole block is web-only.
  if (isNative) { showNativeVersion(); return; }

  // Under Phase E QA the app is served from the build output, which does not
  // contain sw.js — that file lives at the project root and is served by the
  // app's own server in production. Registering here would fail on a fallback
  // HTML response and report "unsupported MIME type ('text/html')" as an app
  // defect on every single run. QA does not exercise PWA delivery, and this is
  // not something an app author could fix.
  if (typeof window !== 'undefined' && window.__APPFACTORY_QA__) return;

  if ('serviceWorker' in navigator) {
    // A brand new install has no controller yet. The SW claiming this page for
    // the first time ALSO fires controllerchange, but there is no stale cached
    // shell to replace — so that first claim must not reload.
    const hadController = !!navigator.serviceWorker.controller;

    navigator.serviceWorker.register('/sw.js').then((reg) => {
      // Check for a newer deploy on every launch. iOS especially will sit on a
      // cached sw.js for a long time otherwise.
      reg.update().catch(() => {});

      // An update downloaded on a previous visit is WAITING. Activate it now, at
      // launch, before the user starts anything — controllerchange then reloads
      // a page nobody was using.
      if (reg.waiting) { reg.waiting.postMessage('skipWaiting'); return; }

      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        nw?.addEventListener('statechange', () => {
          // `controller` being set is what distinguishes "an update arrived
          // during this session" from "this is the first install".
          if (nw.state === 'installed' && navigator.serviceWorker.controller) offerUpdate();
        });
      });
    }).catch(() => {});

    let reloaded = false; // claim() or a waiting SW can fire this more than once
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) return;       // first-ever install claiming this page
      if (reloaded) return;
      if (!isSafeToReload()) { offerUpdate(); return; }
      reloaded = true;
      location.reload();
    });
  }

  // Name the live cache in the footer: a genuine answer to "which build am I
  // running", which matters most when someone reports a bug.
  caches.keys()
    .then((keys) => {
      const live = keys.find((k) => k === WEB_BUILD) ?? keys[0];
      if (live) setVersionText(live);
    })
    .catch(() => {});

  let deferred;
  addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e;
    toast(`Install ${APP_NAME}`, {
      ms: 0,
      onClick: async () => {
        try { await deferred?.prompt(); } catch { /* dismissed */ }
        deferred = null;
      },
    });
  });
}

/**
 * Offer the waiting worker, once.
 *
 * Raised as a toast rather than by un-hiding an element that index.html had to
 * contain — which is what tied the old chassis to a fixed set of ids. `ms: 0`
 * keeps it up: an update prompt that vanishes after two seconds is an update
 * nobody ever applies.
 */
let updateToastId = null;
function offerUpdate() {
  if (updateToastId) return;
  updateToastId = toast('A new version is ready.', {
    ms: 0,
    kind: 'update',
    onClick: () => {
      dismiss(updateToastId);
      updateToastId = null;
      navigator.serviceWorker.getRegistration().then((r) => r?.waiting?.postMessage('skipWaiting'));
      setTimeout(() => location.reload(), 300);
    },
  });
}

async function showNativeVersion() {
  const native = await appVersion();
  setVersionText([native, `web ${WEB_BUILD}`].filter(Boolean).join(' · '));
}

/**
 * Which build is actually running — the honest answer to the first question
 * anyone asks about a bug report.
 *
 * A ref rather than a DOM write, so a settings sheet can display it by reading
 * `buildLabel.value` and the chassis no longer requires an element with a
 * particular id to exist.
 */
export const buildLabel = ref('');
function setVersionText(text) { buildLabel.value = text; }
